<template>
  <div class="editor-container">
    <div v-if="selectedElement" class="card">
      <!-- Utilisation du composant PreviewHeader -->
      <cardHeader :elementLabel="elementLabel" :elementType="selectedElement.el_Type" :mode="'Editor'" />
      <div class="card-body">

        <form @submit.prevent="saveElement">
          <div hidden class="mb-3">
            <label class="form-label">Element Label</label>
            <input type="text" class="form-control" v-model="elementLabel" @input="handleLabelChange(elementLabel)">
          </div>
          <div hidden class="mb-3">
            <label class="form-label">Element ID</label>
            <input type="text" class="form-control" v-model="selectedElement.el_ID" readonly>
          </div>
          <div hidden class="mb-3" v-show="selectedElement.isNewElement">
            <label class="form-label">Type</label>
            <input type="text" class="form-control" v-model="selectedElement.el_Type">
          </div>
          <div class="mb-3">
            <h3>
              <input @input="updateText" placeholder="Main prompt" type="text" class="inputMainPrompt"
                v-model="selectedElement.el_Text" />
            </h3>
          </div>
          <div class="form-group mb-3">
            <div ref="quillEditor" class="quill-editor"></div>
          </div>

          <div class="mb-3">
            <label class="form-label">Enter labels in the correct order:</label>
            <div v-for="(label, index) in selectedElement.labels" :key="index" class="input-group mb-2">
              <input type="text" class="form-control" v-model="selectedElement.labels[index]"
                placeholder="Label text" @input="updateLabels" />
              <button type="button" class="btn btn-danger btn-sm" @click="removeLabel(index)">
                ✖
              </button>
            </div>
            <button type="button" class="btn btn-primary btn-sm" @click="addLabel">
              ➕ Add Label
            </button>
          </div>

          <div class="row">
            <div class="col">
              <div class="form-floating maxScore mt-4">
                <input title="Score Definition" placeholder="1" type="number" name="maxScore" id="maxScore"
                  class="form-control" v-model="selectedElement.maxScore" @input="updateMaxScore"/>
                <label class="ml-2" for="maxScore">Points for correct order</label>
              </div>
            </div>
            <div class="col">
              <div class="form-floating penalties mt-4">
                <input title="Penalties Definition" placeholder="0" type="number" name="penalties" id="penalties"
                  class="form-control" v-model="selectedElement.penalties" @input="updatePenalties" />
                <label class="ml-2" for="penalties">Penalties for incorrect order</label>
              </div>
            </div>

          </div>

          <div class="form-floating feedback mt-4">
            <input Title="Positive feedback" placeholder="Positive feedback" type="text" name="positiveFeedback"
              id="positiveFeedback" class="form-control" v-model="selectedElement.feedback.correct"
              @input="updateFeedback" />
            <label class="ml-2" for="positiveFeedback">Positive feedback</label>
          </div>
          <div class="form-floating feedback mt-4">
            <input title="Negative feedback" placeholder="Negative feedback" type="text" name="negativeFeedback"
              id="negativeFeedback" class="form-control" v-model="selectedElement.feedback.incorrect"
              @input="updateFeedback" />
            <label class="ml-2" for="negativeFeedback">Negative feedback</label>
          </div>


        </form>
      </div>
            
      <!-- Slider de difficulté -->
      <DifficultySlider 
        v-model="selectedElement.difficulty_rating"
        @update:model-value="updateElement('difficulty_rating', $event)"
      />
      
      <dimension v-if="hasDimensions" />
      <div class="card-footer ">
        <EditorActionBar :selectedElement="selectedElement" :elementLabel="elementLabel" @save-element="saveElement"
          :isItemBank />
      </div>
    </div>
    <div v-else class="alert alert-info">
      Select an element to edit
    </div>
  </div>
</template>

<script setup>
import cardHeader from '../default/cardHeader.vue';
import { useTestStore } from '../../../stores/testStore'
import { computed, ref, watch, onMounted } from 'vue'
import Quill from 'quill'
import EditorActionBar from '../default/EditorActionBar.vue';
import dimension from '../default/dimension.vue';

import DifficultySlider from '../../common/DifficultySlider.vue';
// Définir les props Pour l'adaptation de l'action bar footer au contexte : TEST or ITEM BANK
const props = defineProps({
  isItemBank: {
    type: Boolean,
    default: false
  }
});

const store = useTestStore()

// Computed pour vérifier si les dimensions doivent être affichées
const hasDimensions = computed(() => {
  const selectedEl = store.selectedElement;
  const testData = store.testData;
  
  // Afficher si l'élément a déjà des dimensions assignées
  const hasAssignedDimensions = selectedEl?.dimension && selectedEl.dimension.length > 0;
  
  // Afficher si des dimensions sont disponibles pour être ajoutées
  const hasAvailableDimensions = testData?.dimensions && testData.dimensions.length > 0;
  
  return hasAssignedDimensions || hasAvailableDimensions;
});

/* const saveTest = () => {
  store.saveTestData();
}; */

const selectedElement = computed(() => store.selectedElement)
const elementLabel = ref('')
const quillEditor = ref(null) // Référence pour l'éditeur Quill
let quillInstance = null // Instance de Quill

watch(() => store.selectedElementLabel, (newLabel) => {
  elementLabel.value = newLabel || ''
}, { immediate: true })

const handleLabelChange = (newLabel) => {
  /*  if (props.isItemBank) {
     console.log("isItemBank is true. Skipping store.updateElement.");
     return;
   } */
  store.updateElementLabel(newLabel)
}

if (quillInstance && selectedElement.el_RichText) {
  quillInstance.root.innerHTML = selectedElement.el_RichText;
}

const updateText = () => {
  if (props.isItemBank) return;
  store.updateElement(selectedElement.value);
}

// Utility function pour mettre à jour les éléments (comme dans les autres éditeurs)
const updateElement = (key, value) => {
  if (!props.isItemBank) {
    // Sauvegarde automatique uniquement dans le contexte du test
    console.log(`Updating element in test context: key=${key}, value=${value}`);
    if (selectedElement.value) {
      const keys = key.split('.');
      let obj = selectedElement.value;
      while (keys.length > 1) obj = obj[keys.shift()];
      obj[keys[0]] = value;
      store.updateElement(selectedElement.value);
    }
  } else {
    // Logique spécifique à l'Item Bank (si nécessaire)
    console.log("Item Bank context detected. No auto-save triggered.");
  }
};

const saveElement = () => {
  /*  if (props.isItemBank) {
     console.log("isItemBank is true. Skipping store.updateElement.");
     return;
   } */
  if (selectedElement.value) {
    store.updateElement(selectedElement.value)
  }
}

const addLabel = () => {
  selectedElement.value.labels.push('');
  if (props.isItemBank) {
    console.log("isItemBank is true. Skipping store.updateElement.");
    return;
  }
  store.updateElement(selectedElement.value);
};

const removeLabel = (index) => {
  selectedElement.value.labels.splice(index, 1);
  if (props.isItemBank) return;
  store.updateElement(selectedElement.value);
};

const updateMaxScore = () => {
  if (props.isItemBank) return;
  store.updateElement(selectedElement.value);
};

const updatePenalties = () => {
  if (props.isItemBank) return;
  store.updateElement(selectedElement.value);
};

const updateLabels = () => {
  if (props.isItemBank) return;
  store.updateElement(selectedElement.value);
};

const updateFeedback = () => {
  if (props.isItemBank) return;
  store.updateElement(selectedElement.value);
};

// Fonction pour initialiser maxScore si elle n'existe pas
const initializemaxScore = () => {
  if (selectedElement.value && selectedElement.value.maxScore === undefined) {
    selectedElement.value.maxScore = 1; // Valeur par défaut
    if (!props.isItemBank) {
      store.updateElement(selectedElement.value);
    }
  }
};

// Fonction pour initialiser Les pénalités si elles n'existent pas
const initializePenalties = () => {
  if (selectedElement.value && selectedElement.value.penalties === undefined) {
    selectedElement.value.penalties = 0; // Valeur par défaut
    if (!props.isItemBank) {
      store.updateElement(selectedElement.value);
    }
  }
};

// Fonction pour initialiser labels si elles n'existent pas
const initializeLabels = () => {
  if (selectedElement.value && !Array.isArray(selectedElement.value.labels)) {
    selectedElement.value.labels = []; // Valeur par défaut
    if (!props.isItemBank) {
      store.updateElement(selectedElement.value);
    }
  }
};

// Fonction pour initialiser feedback si il n'existe pas
const initializeFeedback = () => {
  if (selectedElement.value && !selectedElement.value.feedback) {
    selectedElement.value.feedback = { correct: '', incorrect: '' }; // Valeur par défaut
    if (!props.isItemBank) {
      store.updateElement(selectedElement.value);
    }
  }
};

// Watcher pour initialiser maxScore lors du changement d'élément
watch(selectedElement, (newElement) => {
  if (newElement) {
    initializemaxScore();
    initializePenalties();
    initializeLabels();
    initializeFeedback();

    // Recharger le contenu Quill si l'instance existe
    if (quillInstance) {
      if (newElement.el_RichText) {
        quillInstance.root.innerHTML = newElement.el_RichText;
      } else {
        quillInstance.root.innerHTML = '';
      }
    }
  }
}, { immediate: true });

// Initialisation de l'éditeur Quill
onMounted(() => {
  if (quillEditor.value) {
    quillInstance = new Quill(quillEditor.value, {
      theme: 'snow',
      modules: {
        toolbar: [
          // Styles de texte
          ['bold', 'italic', 'underline', 'strike'], // Gras, italique, souligné, barré

          // Titres
          [{ header: 1 }, { header: 2 }], // Titres niveau 1 et 2
          [{ header: [1, 2, 3, 4, 5, 6, false] }], // Menu déroulant des tailles de titre

          // Listes et indentations
          [{ list: 'ordered' }, { list: 'bullet' }], // Listes ordonnées et à puces
          [{ indent: '-1' }, { indent: '+1' }], // Indentation

          // Alignement
          [{ align: [] }], // Alignement : gauche, centre, droite, justifié

          // Couleurs
          [{ color: [] }, { background: [] }], // Couleur de texte et d'arrière-plan

          // Médias
          ['image', 'link'], // Insérer une image, un lien, ou une vidéo

          // Nettoyer la mise en forme
          ['clean'], // Bouton pour effacer le formatage
        ],
      },
    })

    // Synchroniser Quill avec les données
    if (selectedElement.value?.el_RichText) {

      quillInstance.root.innerHTML = selectedElement.value.el_RichText
    }

    quillInstance.on('text-change', () => {
      if (selectedElement.value) {
        if (props.isItemBank) {
          console.log("isItemBank is true. Skipping store.updateElement.");
          return;
        }
        selectedElement.value.el_RichText = quillInstance.root.innerHTML
        store.updateElement(selectedElement.value);
      }
    })
  }
})
</script>

<style scoped>
.card-header {
  background-color: #426C9C;
  color: white
}

.sendToItemLibBT {
  margin-left: 10px;
}
</style>